import { ENTITY_TYPES, ENTITY_TYPES_WITH_SOURCE_REPLICATION } from '../../../utils/csq/csqConstants';

function ActionSection({
  entityType,
  setEntityType,
  action,
  setAction,
  availableActions,
  selectedEntityType,
  canListSource,
  resetRun,
  configText,
  setConfigText,
  configError,
  setConfigError,
  resetConfigToDefault,
}) {
  return (
    <div className="demo-env-mgmt-section">
      <div className="demo-env-mgmt-title-with-label">
        <h2 className="demo-env-mgmt-section-title">Action</h2>
        <label className="demo-env-mgmt-label" htmlFor="entity-type">Entity type</label>
        <select
          id="entity-type"
          className="demo-env-mgmt-select"
          style={{ width: 220 }}
          value={entityType}
          onChange={(e) => {
            const nextEntityType = e.target.value;
            setEntityType(nextEntityType);
            setAction(nextEntityType === 'all' ? 'delete' : 'list');
            resetRun();
          }}
        >
          {ENTITY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      {entityType === 'zonings' && (
        <p className="demo-env-mgmt-label" style={{ marginTop: 6, fontWeight: 700 }}>
          Zoning creation resolves its page by NAME on Target — the page MUST already exist.
          If it doesn&apos;t, create pages via Mappings (Create from Source).
        </p>
      )}

      <div className="demo-env-mgmt-columns" style={{ marginTop: 10 }}>
        {/* Source only ever supports List — no Action select needed, there's nothing else to pick */}
        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">Source</h3>
          <p className="demo-env-mgmt-label">
            Action: <strong>List</strong>
          </p>
          {canListSource ? (
            <p className="demo-env-mgmt-label">
              Source is read-only here — Create/Delete always target the Target datasource. Use the
              List button below to inspect what already exists on the Source.
            </p>
          ) : (
            <p className="demo-env-mgmt-label">
              The composite &quot;All entity types&quot; action has no combined List — pick a single
              entity type above to list it on Source.
            </p>
          )}
        </div>

        {/* Target supports the full set of actions (List/Create/Delete/Resync) */}
        <div className="demo-env-mgmt-column">
          <div className="demo-env-mgmt-title-with-label">
            <h3 className="demo-env-mgmt-column-title">Target</h3>
            <label className="demo-env-mgmt-label" htmlFor="action">Action</label>
          </div>
          <div className="demo-env-mgmt-field">
            <select
              id="action"
              className="demo-env-mgmt-select"
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                resetRun();
              }}
            >
              {availableActions.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>

          {entityType === 'all' && (
            <p className="demo-env-mgmt-label" style={{ marginTop: 6 }}>
              Operates across every entity type: mappings/zonings/segments/properties on each Target
              datasource above, plus charts/dashboards on the Project above. Source datasource is used only as
              the source for replicating mappings on Create, and is never itself modified or cleared.
            </p>
          )}

          {entityType !== 'all' && selectedEntityType && selectedEntityType.level === 'datasource' && (action === 'create' || action === 'delete') && (
            <p className="demo-env-mgmt-label" style={{ marginTop: 6 }}>
              {action === 'create' ? 'Creates' : 'Deletes'} {entityType} on every Target datasource above.
              Source datasource is never modified or cleared by Create/Delete.
            </p>
          )}

          {action === 'create' && entityType === 'mappings' && (
            <>
              <h3 className="demo-env-mgmt-section-title" style={{ marginTop: 12 }}>Create from Source</h3>
              <p className="demo-env-mgmt-label">
                Mappings are created dynamically from the Source: every
                mapping is fetched from Source (with all of its pages), and recreated as ONE new mapping
                on Target carrying that same full set of pages/page-groups (category is copied as-is from
                Source, it&apos;s a global catalog shared across projects). If a mapping with the same name
                already exists on Target this fails before creating anything — delete the conflicting
                mapping(s) on Target first and re-run.
              </p>
            </>
          )}

          {action === 'create' && entityType === 'zonings' && (
            <>
              <h3 className="demo-env-mgmt-section-title" style={{ marginTop: 12 }}>Create from Source</h3>
              <p className="demo-env-mgmt-label">
                Zonings are created dynamically from the Source: every
                zoning is fetched from Source, its page is resolved by NAME on Target (page ids differ
                per datasource — the page must already exist on Target, e.g. via mappings &quot;Create from
                Source&quot; first), and a new zoning is created on Target with the same name/device/
                width/height/user agent. Screenshots can&apos;t be copied — they&apos;re captured fresh from
                Target&apos;s own traffic, so this can take a while per zoning. If a zoning with the same
                name already exists on Target this fails before creating anything — delete the conflicting
                zoning(s) on Target first and re-run.
              </p>
            </>
          )}

          {action === 'create' && entityType === 'segments' && (
            <>
              <h3 className="demo-env-mgmt-section-title" style={{ marginTop: 12 }}>Create from Source</h3>
              <p className="demo-env-mgmt-label">
                Segments are created dynamically from the Source: every
                segment is fetched from Source (with its full query definition) and recreated as-is on
                Target. If a segment with the same name already exists on Target this fails before
                creating anything — delete the conflicting segment(s) on Target first and re-run.
              </p>
            </>
          )}

          {action === 'create' && entityType === 'properties' && (
            <>
              <h3 className="demo-env-mgmt-section-title" style={{ marginTop: 12 }}>Create from Source</h3>
              <p className="demo-env-mgmt-label">
                Custom properties are created dynamically from the Source: every property (event and user
                level) is fetched from Source (with its full formula definition) and recreated as-is on
                Target. If a property with the same name already exists on Target this fails before
                creating anything — delete the conflicting propert{'y'}/{'ies'} on Target first and re-run.
              </p>
            </>
          )}

          {action === 'create' && entityType === 'dashboards' && (
            <>
              <h3 className="demo-env-mgmt-section-title" style={{ marginTop: 12 }}>Create from Source</h3>
              <p className="demo-env-mgmt-label">
                Dashboards are created dynamically from the Source: every dashboard on the Source project
                is fetched — including ContentSquare&apos;s built-in template dashboards (RUM, Error
                Monitoring, etc.), which use the same model as custom ones — and recreated as a new
                dashboard on Target with the same name, widget groups (sections), and widgets
                (charts/KPIs/tables/text). This always adds new dashboards; it does not deduplicate
                against existing Target dashboards with the same name.
              </p>
            </>
          )}

          {action === 'resync' && (
            <>
              <h3 className="demo-env-mgmt-section-title" style={{ marginTop: 12 }}>Resync (Delete+Create)</h3>
              <p className="demo-env-mgmt-label">
                Deletes every existing {entityType === 'all' ? 'entity of every type' : entityType} on the
                Target{selectedEntityType && selectedEntityType.level === 'datasource' ? ' datasource(s)' : ''} above,
                then recreates {entityType === 'all' ? 'everything' : 'it'}
                {ENTITY_TYPES_WITH_SOURCE_REPLICATION.includes(entityType) || entityType === 'all'
                  ? ' from the Source'
                  : ' using its default payload'}. The preview below only lists what would be
                deleted — the recreation step runs automatically after you confirm and is not
                previewed. This all happens in a single confirm step — if it fails partway through
                (after deleting but before finishing recreation), Target is left with whatever
                completed so far and a warning is logged; simply re-run Resync (or Create) to finish.
              </p>
            </>
          )}

          {/* Manual JSON payload editor — still used for charts (the only entity type without a
              "Create from Source" replication flow). Mappings/zonings/segments/properties/dashboards
              use their dynamic "Create from Source" flow above instead of a hand-edited payload (see
              also handlePreview/handleConfirm's `create-mappings-from-source`/`create-zonings-from-source`/
              `create-segments-from-source`/`create-properties-from-source`/`create-dashboards-from-source`
              branches in entities-tab.js). */}
          {action === 'create' && entityType !== 'all' && !ENTITY_TYPES_WITH_SOURCE_REPLICATION.includes(entityType) && (
            <>
              <h3 className="demo-env-mgmt-section-title" style={{ marginTop: 12 }}>Create payload</h3>
              <p className="demo-env-mgmt-label">
                Pre-filled with the default payload for this entity type. Edit it if you need a different
                configuration — it is sent as-is when you confirm.
              </p>
              <textarea
                className="demo-env-mgmt-textarea"
                value={configText}
                onChange={(e) => {
                  setConfigText(e.target.value);
                  setConfigError(null);
                }}
                spellCheck={false}
              />
              {configError && <p className="demo-env-mgmt-error">{configError}</p>}
              <div className="demo-env-mgmt-actions">
                <button
                  type="button"
                  className="demo-env-mgmt-btn demo-env-mgmt-btn-secondary"
                  onClick={resetConfigToDefault}
                >
                  Reset to default
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ActionSection;
